import {QueryErrorResetBoundary} from "@tanstack/react-query";
import {AxiosError} from "axios";
import {useEffect} from "react";
import {useLocation, useNavigate} from "react-router-dom";

import {Toaster} from "@/components/ui/sonner";
import useInit from "@/src/hooks/useInit";
import {
  AuthErrorHandler,
  ChangePasswordModal,
  CompletePlaceholderProfileModal,
} from "@/src/modules/auth/components";
import {useIsAuthenticated} from "@/src/modules/auth/store/session";
import {useSessionEndRedirect} from "@/src/modules/auth/hooks/use-session-end-redirect";
import {useSessionInvalidatedRealtime} from "@/src/modules/auth/hooks/use-session-invalidated-realtime";
import {
  AuthenticatedAppShell,
  BlockingGateRenderer,
  Layout,
  Loader,
  ResidentHouseholdForbiddenDialog,
} from "@/src/modules/core/components";
import {FormDraftResume} from "@/src/modules/core/components/form-draft-resume";
import {GeolocationProvider} from "@/src/modules/core/components/geolocation-provider";
import {GlitchTipErrorBoundary} from "@/src/modules/core/components/glitchtip-error-boundary";
import {GlitchTipRouteSync} from "@/src/modules/core/components/glitchtip-route-sync";
import {GlitchTipUserSync} from "@/src/modules/core/components/glitchtip-user-sync";
import {clearReloadGuard} from "@/src/modules/core/lib/app-reload-coordinator";
import {useCheckConnection, useMe, useNewVersion} from "@/src/modules/core/hooks";
import {HelpProvider} from "@/src/modules/help/components/help-provider";
import {ForcedReadGate} from "@/src/modules/briefing-boards/components/forced-read-gate";
import {
  BreakActiveAlert,
  BreakNoticeAlert,
  ShiftAutocompletedAlert,
  ShiftEndAlert,
} from "@/src/modules/officer/components";
import {PanicIncomingAlert} from "@/src/modules/panic-alerts/components/panic-incoming-alert";
import {useActiveShift} from "@/src/modules/officer/hooks";
import {
  useCheckNavigation,
  useRedirectAfterMe,
  useRedirectToLogout,
  useRequirePropertiesNavigation,
} from "@/src/modules/routing/hooks";
import {
  AuthenticatedRoutes,
  LogoutRoute,
  UnauthenticatedRoutes,
} from "@/src/modules/routing/routes";
import {shiftsService} from "@/src/modules/shifts/services";
import {useNotificationFeedRealtime} from "@/src/modules/notification-feed/hooks/use-notification-feed-realtime";
import {useDirectMessagesRealtime} from "@/src/modules/direct-messages/hooks/use-direct-messages-realtime";
import {handlePushNotificationClick} from "@/src/modules/notification-feed/lib/handle-push-notification-click";
import {usePushNotificationDeepLink} from "@/src/modules/notification-feed/hooks/use-push-notification-deep-link";
import {initNotificationAttentionAudio} from "@/src/modules/notification-feed/lib/notification-attention";
import {PushSubscriptionSync} from "@/src/modules/notifications/components/push-subscription-sync";
import useImpersonationRecovery from "@/src/modules/support/hooks/use-impersonation-recovery";

import "../index.css";
import {useCompany} from "../modules/company/hooks";
import {SocketProvider, useSocketConnect} from "../modules/core/context/socket";
import {Me} from "../modules/core/types";

function Init({me, children}: {me: Me; children: React.ReactNode}) {
  // All authenticated users (including Admin) need private-user WS for session:invalidated
  // (e.g. user_logout on another tab). Shift-only UI stays gated below.
  useSocketConnect({enabled: true});
  useNotificationFeedRealtime();
  useDirectMessagesRealtime();
  useSessionInvalidatedRealtime();

  useEffect(() => {
    initNotificationAttentionAudio();
  }, []);
  // TODO: sacar cuando termine los refactors y se migren los officers
  const {isPending: isPendingInit} = useInit();

  if (isPendingInit) {
    return <Loader className="h-screen" />;
  }

  return children;
}

function ServiceWorkerNavigationBridge() {
  const navigate = useNavigate();

  useEffect(() => {
    if (!("serviceWorker" in navigator)) {
      return;
    }

    function onServiceWorkerMessage(event: MessageEvent) {
      const payload = event.data as
        | {kind?: string; to?: string; notificationData?: Record<string, unknown>}
        | undefined;
      if (!payload || payload.kind !== "navigate" || typeof payload.to !== "string") {
        return;
      }
      const notificationData =
        payload.notificationData && typeof payload.notificationData === "object"
          ? payload.notificationData
          : {};
      handlePushNotificationClick(notificationData, navigate, payload.to);
    }

    navigator.serviceWorker.addEventListener("message", onServiceWorkerMessage);
    return () => {
      navigator.serviceWorker.removeEventListener("message", onServiceWorkerMessage);
    };
  }, [navigate]);

  return null;
}

function Authenticated() {
  useCheckConnection();
  useImpersonationRecovery();
  useRedirectAfterMe();
  usePushNotificationDeepLink();
  useRequirePropertiesNavigation();
  useCheckNavigation();
  const location = useLocation();
  const {isPending: isMePending, data: meData} = useMe();
  const {isPending: isPendingCompany} = useCompany();
  const {isPending: isActiveShiftPending} = useActiveShift();
  const isShiftsEnabledForRole = shiftsService.getIsShiftsEnabledForRole();

  // Do not use useMe().isFetching here: background refetches (e.g. after mutations) would
  // swap the whole app for a loader, unmount routes, and retrigger redirect effects.
  if (isMePending || isActiveShiftPending || isPendingCompany) {
    return <Loader className="h-screen" />;
  }

  if (!isMePending && !meData) {
    return <LogoutRoute />;
  }

  const isBareRoute =
    location.pathname === "/location-denied" ||
    location.pathname === "/logout" ||
    location.pathname.startsWith("/resident-claim");

  if (isBareRoute) {
    return <AuthenticatedRoutes />;
  }

  return (
    <SocketProvider>
      <ServiceWorkerNavigationBridge />
      <Init me={meData}>
        <GlitchTipUserSync me={meData} />
        <PushSubscriptionSync me={meData} />
        <GeolocationProvider>
          <HelpProvider>
            <FormDraftResume />
            <BlockingGateRenderer />
            <ResidentHouseholdForbiddenDialog />
            <ChangePasswordModal />
            <CompletePlaceholderProfileModal />
            <ForcedReadGate />
            <PanicIncomingAlert />
            {isShiftsEnabledForRole ? (
              <>
                <BreakActiveAlert />
                <BreakNoticeAlert />
                <ShiftEndAlert />
                <ShiftAutocompletedAlert />
              </>
            ) : null}
            <AuthenticatedAppShell>
              <Layout>
                <AuthenticatedRoutes />
              </Layout>
            </AuthenticatedAppShell>
          </HelpProvider>
        </GeolocationProvider>
      </Init>
    </SocketProvider>
  );
}

export default function App() {
  useNewVersion();
  useRedirectToLogout();
  useSessionEndRedirect();
  const location = useLocation();
  const isAuthenticated = useIsAuthenticated();

  useEffect(() => {
    const timer = window.setTimeout(() => {
      clearReloadGuard();
    }, 8_000);
    return () => window.clearTimeout(timer);
  }, []);

  return (
    <>
      <Toaster richColors />
      <GlitchTipRouteSync />
      <QueryErrorResetBoundary>
        {({reset}) => (
          <GlitchTipErrorBoundary
            fallback={({error, resetError}) => (
              <AuthErrorHandler
                error={error as Error | AxiosError}
                resetErrorBoundary={() => {
                  reset();
                  resetError();
                }}
              />
            )}
          >
            {isAuthenticated ? (
              <Authenticated />
            ) : (
              <UnauthenticatedRoutes />
            )}
          </GlitchTipErrorBoundary>
        )}
      </QueryErrorResetBoundary>
    </>
  );
}
